import type { WorkOrderChangeObjectItem, WorkOrderObjectData } from 'gql/graphql';
import { ChapterEnum } from 'gql/graphql';
import type { IFilterVariables } from 'pages/Project/UerProject/WorkOrder/AddWorkOrderUer/helpersAndTypes';

import type { ColDef, ICellRendererParams, IDatasource } from 'ag-grid-community';
import * as R from 'ramda';
import type { Dispatch, SetStateAction } from 'react';

export const DEFAULT_ADD_UER_STEPS = [
    {
        stepId: 0,
        label: 'Выбор объекта',
    },
    {
        stepId: 1,
        label: 'Выбор расценок',
    },
];

export const CHAPTER_ITEMS = [
    { id: ChapterEnum.Chapter1_7, label: 'Глава 1-7' },
    { id: ChapterEnum.Chapter8, label: 'Глава 8' },
    { id: ChapterEnum.Chapter9, label: 'Глава 9' },
];

export interface IObjectTabFilters {
    chapter?: (typeof CHAPTER_ITEMS)[number] | null;
    object?: {
        id: number;
        label: string;
    };
}

export type StepsContent = Record<
    number,
    (onStepClickCreator: (step: (typeof DEFAULT_ADD_UER_STEPS)[number]) => () => void) => JSX.Element
>;

export type TControlRef = {
    refreshAddDataTable: (() => void) | undefined;
    setCheckedPrices: Dispatch<SetStateAction<{ [p: number]: boolean }>>;
};

export type TCommonObj = { id: number; parentId: number; isLeaf: boolean; name: string };

export interface IAddWorkOrderPriceProps<T extends { id?: number; parentId: number; name: string }> {
    constructionTypeId: number;
    objects: T[];
    onClose: () => void;
    stepsContent: StepsContent;
    getItemDisabled?: (isDisabledFirstStep?: boolean) => () => boolean;
    isLoading?: boolean;
    dataSource: IDatasource;
    onSearchStringClear: () => void;
    searchString: string;
    onSearch: ({ value }: any) => void;
    onSearchStringChange: ({ value }: any) => void;
    filters: Record<string, any>;
    checkedDataSender: (checked: {
        checkedLeafObjects: number[];
        uerRowBaseRowIds: number[];
        objectTabFilters: IObjectTabFilters;
    }) => void;
    initiallyCheckedLeafs?: Record<number, boolean>;
    initiallyCheckedObj?: T;
    extraColumnDefs?: ColDef[];
    getObjItemLabel?: (item: { id: any; label: string }) => string | JSX.Element;
    getObjItemDisabled?: (item: { id: any; label: string }) => boolean;
    filterVariables?: IFilterVariables;
    changeFilterHandler?: (params: { fieldName: string; checkedValues: string | string[] }) => void;
}

export interface IAddPricesToWorkOrderProps {
    workOrderObjectIds: number[];
    chapter: ChapterEnum | undefined;
    workOrderId: number;
    constructionTypeId: number;
    uerRowBaseRowIds: number[];
    onClose: () => void;
    reloadWorkOrderTable: () => void;
}

export interface IAddWorkOrderUerTabContentProps<T extends TCommonObj[]> {
    activeStep: (typeof DEFAULT_ADD_UER_STEPS)[number] | undefined;
    workOrderObjects: T | null | undefined;
    constructionTypeId: number;
    objectTabFilters: IObjectTabFilters;
    onChangeObjectTabFilters: (value: IObjectTabFilters) => void;
    onChangeCheckedObjects: (value: { [key: number]: boolean }) => void;
    onChangeCheckedPrices: (value: { [key: number]: boolean }) => void;
    checkedObjects: { [key: number]: boolean | 'intermediate' };
    checkedPrices: { [key: number]: boolean };
    getObjItemDisabled?: (item: { id: any; label: string }) => boolean;
    getObjItemLabel?: (item: { id: any; label: string }) => string | JSX.Element;
    dataSource: IDatasource;
    onSearchStringClear: () => void;
    searchString: string;
    onSearch: ({ value }: any) => void;
    onSearchStringChange: ({ value }: any) => void;
    filters: Record<string, any>;
    extraColumnDefs?: ColDef[];
    filterVariables?: IFilterVariables;
    changeFilterHandler?: (params: { fieldName: string; checkedValues: string | string[] }) => void;
    isOrderChange?: boolean;
}

export type IObjectTabProps = Pick<
    IAddWorkOrderUerTabContentProps<any>,
    | 'workOrderObjects'
    | 'constructionTypeId'
    | 'objectTabFilters'
    | 'onChangeObjectTabFilters'
    | 'checkedObjects'
    | 'onChangeCheckedObjects'
    | 'getObjItemDisabled'
    | 'getObjItemLabel'
>;
export type IPriceTabProps = Pick<
    IAddWorkOrderUerTabContentProps<any>,
    | 'checkedPrices'
    | 'onChangeCheckedPrices'
    | 'dataSource'
    | 'onSearchStringClear'
    | 'searchString'
    | 'onSearch'
    | 'onSearchStringChange'
    | 'filters'
    | 'extraColumnDefs'
    | 'filterVariables'
    | 'changeFilterHandler'
>;

export type IObjectTableColumnsProps = Pick<
    IAddWorkOrderUerTabContentProps<any>,
    'checkedObjects' | 'onChangeCheckedObjects'
>;
export type IPriceTableColumnsProps = Pick<
    IAddWorkOrderUerTabContentProps<any>,
    | 'checkedPrices'
    | 'onChangeCheckedPrices'
    | 'extraColumnDefs'
    | 'filterVariables'
    | 'changeFilterHandler'
    | 'isOrderChange'
>;

export type IObjectTabDataProps = Pick<
    IAddWorkOrderUerTabContentProps<any>,
    'workOrderObjects' | 'objectTabFilters' | 'constructionTypeId'
>;

export type ICheckedObjectsCellRendererProps = IObjectTableColumnsProps & ICellRendererParams;
export type ICheckedPricesCellRendererProps = IPriceTableColumnsProps & ICellRendererParams;

export const getStepLabel = (item: (typeof DEFAULT_ADD_UER_STEPS)[number]) => item?.label;
export const getItemDisabledDefault = (isDisabled?: boolean) => (item: (typeof DEFAULT_ADD_UER_STEPS)[number]) =>
    isDisabled && item?.stepId === 1;
export const getItemCompleted =
    (activeStep: (typeof DEFAULT_ADD_UER_STEPS)[number]) => (item: (typeof DEFAULT_ADD_UER_STEPS)[number]) =>
        item?.stepId < activeStep?.stepId;

const getParentProp = (item: any) => item.parentId;

export const groupWorkOrderObjectsByParentId = R.groupBy(getParentProp);
const makeObjectsTree = (tree: Record<string, WorkOrderChangeObjectItem[]>, parentId: number, level = 1) =>
    (tree[parentId] ?? []).reduce((acc: any, item: WorkOrderChangeObjectItem) => {
        const children = makeObjectsTree(tree, item.id, level + 1);

        acc.push({ ...item, level }, ...children);

        return acc;
    }, []);

export const getObjectsTree = (
    items: WorkOrderChangeObjectItem[],
    rootId: number,
): (WorkOrderObjectData & { level: number })[] => {
    const grouped = groupWorkOrderObjectsByParentId(items);

    return makeObjectsTree(grouped, rootId);
};
